import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Entity,
  GroupEntity,
  UserEntity,
  isGroupEntity,
  isUserEntity,
} from '@backstage/catalog-model';

export const USERS_QUERY_KEY = 'users' as const;
export const USER_BY_EMAIL_QUERY_KEY = 'userByEmail' as const;
export const COMPONENTS_QUERY_KEY = 'components' as const;
export const GROUPS_QUERY_KEY = 'groups' as const;
export const ENTITY_BY_REF_QUERY_KEY = 'entityByRef' as const;

/**
 * Query hook for getting all users from the catalog
 */
export const useGetUsers = (): UseQueryResult<UserEntity[], Error> => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: [USERS_QUERY_KEY],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { Kind: 'User' },
      });
      return items.filter((entity): entity is UserEntity =>
        isUserEntity(entity),
      );
    },
  });
};

/**
 * Query hook for getting a user by email from the catalog.
 * Pass undefined to skip the query (e.g., when entity is already available).
 */
export const useGetUserByEmail = (
  email: string | undefined,
): UseQueryResult<UserEntity | null, Error> => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: [USER_BY_EMAIL_QUERY_KEY, email],
    queryFn: async () => {
      if (!email) {
        return null;
      }
      const { items } = await catalogApi.getEntities({
        filter: { 'spec.profile.email': email },
      });

      if (items.length > 0 && isUserEntity(items[0])) {
        return items[0];
      }
      return null;
    },
    enabled: !!email,
  });
};

/**
 * Query hook for getting all groups from the catalog
 */
export const useGetGroups = (): UseQueryResult<GroupEntity[], Error> => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: [GROUPS_QUERY_KEY],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { Kind: 'Group' },
      });
      return items.filter((entity): entity is GroupEntity =>
        isGroupEntity(entity),
      );
    },
  });
};

/**
 * Query hook for a single catalog entity by ref. Cached by React Query so
 * repeated mounts (e.g. plugin page headers) reuse the same lookup.
 */
export const useGetEntityByRef = (
  entityRef: string | undefined,
): UseQueryResult<Entity | null, Error> => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: [ENTITY_BY_REF_QUERY_KEY, entityRef],
    queryFn: async () => {
      if (!entityRef) {
        return null;
      }
      return (await catalogApi.getEntityByRef(entityRef)) ?? null;
    },
    enabled: !!entityRef,
  });
};

/**
 * Returns a Map from user email to their full entity ref string
 * (e.g. "user:default/john-doe" or "user:custom-ns/john-doe").
 * Preserves the original namespace so links work for non-default namespaces.
 * Backed by useGetUsers(), so cached by React Query.
 */
export const useUserEmailToEntityRef = (): Map<string, string> => {
  const { data: users } = useGetUsers();
  return useMemo(
    () =>
      new Map(
        (users ?? [])
          .filter(u => u.spec.profile?.email)
          .map(u => [
            u.spec.profile!.email!,
            `user:${u.metadata.namespace ?? 'default'}/${u.metadata.name}`,
          ]),
      ),
    [users],
  );
};

/**
 * Returns a Map from group metadata.name to their full entity ref string
 * (e.g. "group:default/team-platform" or "group:custom-ns/team-platform").
 * Preserves the original namespace so links work for non-default namespaces.
 * Backed by useGetGroups(), so cached by React Query.
 */
export const useGroupNameToEntityRef = (): Map<string, string> => {
  const { data: groups } = useGetGroups();
  return useMemo(
    () =>
      new Map(
        (groups ?? []).map(g => [
          g.metadata.name,
          `group:${g.metadata.namespace ?? 'default'}/${g.metadata.name}`,
        ]),
      ),
    [groups],
  );
};

/**
 * Query hook for getting all components from the catalog
 */
export const useGetComponents = (
  excludeComponents: string[] = [],
): UseQueryResult<string[], Error> => {
  const catalogApi = useApi(catalogApiRef);
  return useQuery({
    queryKey: [COMPONENTS_QUERY_KEY, excludeComponents],
    queryFn: async () => {
      const { items } = await catalogApi.getEntities({
        filter: { Kind: 'Component', 'spec.type': 'component' },
      });
      return items
        .map(entity => entity.metadata.name)
        .filter(name => !excludeComponents.includes(name))
        .sort();
    },
  });
};
